import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import Header from './Header';
import './App.css';

const API_BASE = window.location.origin;
const SOCKET_URL = window.location.origin;

function ActiveDevPage() {
  const navigate = useNavigate();
  const [devGroups, setDevGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const launchesRef = useRef(new Map());
  const reloadTimeoutRef = useRef(null);
  const pendingReloadRef = useRef(false);

  // Initial Load
  useEffect(() => {
    loadActiveDevs();
    setupSocket();

    return () => {
      if (socket) {
        socket.disconnect();
      }
      // Cleanup timeout
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
      }
    };
  }, []);

  // Socket Setup
  useEffect(() => {
    if (socket) {
      socket.on('connect', () => {
        const now = new Date().toISOString();
        console.log(`[${now}] [ACTIVE DEV SOCKET] ✅ Connected to server`);
        setIsConnected(true);
      });

      socket.on('disconnect', () => {
        const now = new Date().toISOString();
        console.log(`[${now}] [ACTIVE DEV SOCKET] ❌ Disconnected from server`);
        setIsConnected(false);
      });

      // Market Cap Updates (falls sich MC ändert)
      socket.on('market-cap-update', (update) => {
        handleMarketCapUpdate(update);
      });

      // Dev Trade Updates (neue Trades)
      socket.on('dev-trade-update', (update) => {
        const now = new Date().toISOString();
        console.log(`[${now}] [ACTIVE DEV SOCKET] 📡 Dev Trade Update empfangen: ${update.tokenmint?.substring(0, 8) || 'UNKNOWN'}... | Creator: ${update.creator?.substring(0, 8) || 'UNKNOWN'}... | Type: ${update.trade?.txType || 'N/A'}`);
        handleDevTradeUpdate(update);
      });

      return () => {
        socket.off('connect');
        socket.off('disconnect');
        socket.off('market-cap-update');
        socket.off('dev-trade-update');
      };
    }
  }, [socket]);

  const setupSocket = () => {
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });
    setSocket(newSocket);
  };

  const loadActiveDevs = async (retryCount = 0) => {
    try {
      setError(null);
      const response = await axios.get(`${API_BASE}/api/dev-launches/realtime`, {
        timeout: 30000, // Erhöht von 10s auf 30s
        validateStatus: function (status) {
          return status >= 200 && status < 300;
        }
      });
      
      const launchesData = response.data.data || response.data.launches || [];
      
      if (Array.isArray(launchesData)) {
        // Gruppiere nach Dev (creator) und filtere nur Devs mit Trades
        const devMap = new Map();
        
        launchesData.forEach(launch => {
          if (!launch.creator) return;
          
          const devTrades = launch.dev_trades || [];
          // WICHTIG: Nur Coins MIT dev_trades anzeigen!
          if (devTrades.length === 0) return;
          
          if (!devMap.has(launch.creator)) {
            devMap.set(launch.creator, {
              creator: launch.creator,
              launches: [],
              lastTradeTimestamp: 0
            });
          }
          
          const devGroup = devMap.get(launch.creator);
          devGroup.launches.push(launch);
          
          // Finde neuesten Trade für diesen Dev
          devTrades.forEach(trade => {
            const tradeTimestamp = trade.timestamp || 0;
            if (tradeTimestamp > devGroup.lastTradeTimestamp) {
              devGroup.lastTradeTimestamp = tradeTimestamp;
            }
          });
        });
        
        // WICHTIG: Filtere Devs raus, die KEINE Coins mit Trades haben!
        // Nur Devs mit mindestens einem Coin mit Trades behalten
        const devsWithTrades = Array.from(devMap.values()).filter(devGroup => {
          // Prüfe ob mindestens ein Coin Trades hat
          return devGroup.launches.some(launch => {
            const trades = launch.dev_trades || [];
            return trades.length > 0;
          });
        });
        
        // Konvertiere zu Array und sortiere nach letztem Trade-Zeitpunkt
        const sortedDevs = devsWithTrades.sort((a, b) => {
          return b.lastTradeTimestamp - a.lastTradeTimestamp; // Neueste zuerst
        });
        
        setDevGroups(sortedDevs);
        
        // Update Ref
        launchesRef.current.clear();
        launchesData.forEach(launch => {
          launchesRef.current.set(launch.tokenmint, launch);
        });
        
        setLoading(false);
      } else {
        throw new Error('Invalid data format');
      }
    } catch (err) {
      const errorMsg = err.message || err.toString() || 'Unknown error';
      const errorCode = err.code || 'NO_CODE';
      console.error('[ACTIVE DEV API] Error:', errorMsg, 'Code:', errorCode);
      
      // Retry logic
      if ((err.code === 'ERR_NETWORK' || err.message?.includes('CONTENT_LENGTH_MISMATCH') || err.code === 'ERR_CONTENT_LENGTH_MISMATCH' || err.code === 'ECONNABORTED' || errorCode === 'ECONNABORTED') && retryCount < 3) {
        console.log(`[ACTIVE DEV API] Retrying... (${retryCount + 1}/3)`);
        await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1))); // Längere Wartezeit
        return loadActiveDevs(retryCount + 1);
      }
      
      // Bei Timeout-Fehler: Nicht als kritischen Fehler behandeln, sondern nur loggen
      if (err.code === 'ECONNABORTED' || errorCode === 'ECONNABORTED' || err.message?.includes('timeout') || errorMsg.includes('timeout')) {
        console.warn('[ACTIVE DEV API] Timeout - versuche später erneut...');
        // Versuche nach 5 Sekunden erneut (nur wenn nicht loading)
        if (!loading) {
          setTimeout(() => {
            loadActiveDevs().catch(() => {
              // Ignore
            });
          }, 5000);
        }
        return; // Beende hier, setze keinen Error
      }
      
      setError(errorMsg);
      setLoading(false);
    }
  };

  const handleMarketCapUpdate = (update) => {
    // Debounced reload - nicht sofort, sondern nach kurzer Pause
    scheduleReload();
  };

  const handleDevTradeUpdate = (update) => {
    // Debounced reload - nicht sofort, sondern nach kurzer Pause
    scheduleReload();
  };

  // Debounced reload function - verhindert zu viele gleichzeitige Requests
  const scheduleReload = () => {
    // Wenn bereits ein Reload geplant ist, überspringe
    if (pendingReloadRef.current) {
      return;
    }

    // Lösche vorherigen Timeout falls vorhanden
    if (reloadTimeoutRef.current) {
      clearTimeout(reloadTimeoutRef.current);
    }

    // Markiere als pending
    pendingReloadRef.current = true;

    // Plane Reload nach 2 Sekunden (wartet auf weitere Updates)
    reloadTimeoutRef.current = setTimeout(() => {
      pendingReloadRef.current = false;
      loadActiveDevs().catch(err => {
        console.error('[ACTIVE DEV] Error reloading after update:', err);
        pendingReloadRef.current = false;
      });
    }, 2000); // 2 Sekunden Debounce
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'N/A';
    const now = Date.now();
    const time = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;
    const diff = now - time;
    
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      const remainingHours = hours % 24;
      if (remainingHours > 0) {
        return `${days}d ${remainingHours}h ago`;
      }
      return `${days}d ago`;
    } else if (hours > 0) {
      const remainingMinutes = minutes % 60;
      if (remainingMinutes > 0) {
        return `${hours}h ${remainingMinutes}m ago`;
      }
      return `${hours}h ago`;
    } else if (minutes > 0) {
      return `${minutes}m ago`;
    } else {
      return `${seconds}s ago`;
    }
  };

  const formatNumber = (num) => {
    const value = parseFloat(num);
    if (value >= 1000000) {
      return (value / 1000000).toFixed(2) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(2) + 'K';
    }
    return value.toFixed(2);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="App">
      <Header />

      <main className="app-main">
        <div className="page-header">
          <h2 className="page-title">Active Devs</h2>
          <p className="page-subtitle">
            Devs who are actively trading their own coins. Sorted by most recent trade activity.
          </p>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading active devs...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>Error: {error}</p>
            <button className="retry-btn" onClick={() => loadActiveDevs()}>
              Retry
            </button>
          </div>
        ) : devGroups.length === 0 ? (
          <div className="empty-container">
            <p>No active devs found</p>
          </div>
        ) : (
          <>
            <div className="single-column-layout">
              <div className="column-header">
                <h2>ACTIVE DEVS (Sorted by Last Trade)</h2>
              </div>
              
              <div className="launches-list">
                {devGroups.map((devGroup) => {
                  // WICHTIG: Filtere NUR Coins MIT Trades und sortiere nach neuestem Trade
                  const launchesWithTrades = devGroup.launches.filter(launch => {
                    const trades = launch.dev_trades || [];
                    return trades.length > 0;
                  });
                  
                  // Sortiere nach neuestem Trade
                  const sortedLaunches = launchesWithTrades.sort((a, b) => {
                    const aTrades = a.dev_trades || [];
                    const bTrades = b.dev_trades || [];
                    const aLatest = aTrades.length > 0 ? (aTrades[0].timestamp || 0) : 0;
                    const bLatest = bTrades.length > 0 ? (bTrades[0].timestamp || 0) : 0;
                    return bLatest - aLatest; // Neueste zuerst
                  });

                  return sortedLaunches.map((launch) => {
                    const trades = launch.dev_trades || [];
                    // WICHTIG: Nur Coins MIT Trades anzeigen!
                    if (trades.length === 0) return null;

                    return (
                      <div key={launch.tokenmint} className="launch-row active-dev-row">
                        {/* Desktop Layout - Coin Box wie DEX */}
                        <div className="desktop-layout">
                          <div className="row-time">
                            <div className="row-time-left">
                              {formatTimeAgo(launch.created_at_timestamp || launch.created_at)}
                              {launch.imageurl && (
                                <img 
                                  src={launch.imageurl} 
                                  alt={launch.symbol || launch.name || 'Coin'}
                                  className="coin-image-small"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              )}
                            </div>
                          </div>
                          
                          <div className="row-symbol">
                            <div className="symbol-name">{launch.symbol || launch.name || 'N/A'}</div>
                            <div 
                              className="row-ca clickable"
                              onClick={() => copyToClipboard(launch.tokenmint)}
                              title="Click to copy"
                            >
                              {launch.tokenmint.substring(0, 8)}...{launch.tokenmint.substring(launch.tokenmint.length - 4)}
                            </div>
                          </div>
                          
                          <div className="row-dev">
                            <div className="dev-label">DEV</div>
                            <div 
                              className="dev-ca clickable"
                              onClick={() => {
                                copyToClipboard(launch.creator);
                                navigate(`/dev/${encodeURIComponent(launch.creator)}`);
                              }}
                              title="Click to copy and view dev details"
                            >
                              {launch.creator.substring(0, 8)}...{launch.creator.substring(launch.creator.length - 4)}
                            </div>
                            <div className="bonding-rate-small">{parseFloat(launch.bonding_rate_percent || 0).toFixed(1)}%</div>
                            <div className="last-trade-indicator">
                              Last trade: {formatTimeAgo(devGroup.lastTradeTimestamp)}
                            </div>
                          </div>
                          
                          <div className="row-actions">
                            <a 
                              href={`https://pump.fun/coin/${launch.tokenmint}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="action-btn-small pump-btn"
                            >
                              🚀 Pump.fun
                            </a>
                            <a 
                              href={`https://neo.bullx.io/terminal?chainId=1399811149&address=${launch.tokenmint}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="action-btn-small bullx-btn"
                            >
                              📊 BullX
                            </a>
                          </div>
                          
                          <div className="row-mc">
                            <div className="mc-label-small">CURRENT MC</div>
                            <div className={`mc-value-large green ${launch._updateAnimation ? 'updating' : ''}`}>
                              ${formatNumber(launch.currentmarketcapsol_usd || 0)}
                            </div>
                            <div className="ath-label-small">ATH</div>
                            <div className="ath-value-medium">${formatNumber(launch.peakmarketcapsol_usd || 0)}</div>
                          </div>
                        </div>
                        
                        {/* Dev Trades Table - Desktop */}
                        <div className="dev-trades-table-desktop">
                          <div className="trades-table-header">
                            <div className="trades-header-type">Type</div>
                            <div className="trades-header-sol">SOL Amount</div>
                            <div className="trades-header-tokens">Token Amount</div>
                            <div className="trades-header-mc">Market Cap</div>
                            <div className="trades-header-time">Time</div>
                          </div>
                          <div className="trades-table-body">
                            {trades.slice(0, 10).map((trade, idx) => (
                              <div key={idx} className={`trades-table-row ${trade.txType === 'buy' ? 'trade-row-buy' : 'trade-row-sell'}`}>
                                <div className="trades-cell-type">
                                  <span className={`trade-type-badge ${trade.txType === 'buy' ? 'trade-buy' : 'trade-sell'}`}>
                                    {trade.txType.toUpperCase()}
                                  </span>
                                </div>
                                <div className="trades-cell-sol">{trade.solAmount?.toFixed(4) || '0.0000'} SOL</div>
                                <div className="trades-cell-tokens">{formatNumber(trade.tokenAmount || 0)}</div>
                                <div className="trades-cell-mc">
                                  {trade.marketCapSol > 0 ? (() => {
                                    // Berechne SOL-Preis aus Launch-Daten oder verwende Standardwert
                                    const solPrice = launch.currentmarketcapsol_usd && launch.currentmarketcapsol 
                                      ? parseFloat(launch.currentmarketcapsol_usd) / parseFloat(launch.currentmarketcapsol)
                                      : 150; // Fallback SOL-Preis
                                    const marketCapUsd = trade.marketCapSol * solPrice;
                                    return `$${formatNumber(marketCapUsd)}`;
                                  })() : 'N/A'}
                                </div>
                                <div className="trades-cell-time">{formatTimeAgo(trade.timestamp)}</div>
                              </div>
                            ))}
                            {trades.length > 10 && (
                              <div className="trades-table-more">+{trades.length - 10} more trades</div>
                            )}
                          </div>
                        </div>
                        
                        {/* Mobile Layout */}
                        <div className="mobile-layout">
                          <div className="mobile-time-top">
                            {formatTimeAgo(launch.created_at_timestamp || launch.created_at)}
                          </div>
                          
                          {launch.imageurl && (
                            <div className="mobile-image-container">
                              <img 
                                src={launch.imageurl} 
                                alt={launch.symbol || launch.name || 'Coin'}
                                className="coin-image-mobile"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          
                          <div className="mobile-symbol">
                            {launch.symbol || launch.name || 'N/A'}
                          </div>
                          
                          <div 
                            className="mobile-tokenmint clickable"
                            onClick={() => copyToClipboard(launch.tokenmint)}
                            title="Click to copy"
                          >
                            {launch.tokenmint}
                          </div>
                          
                          <div className="mobile-mc-ath-row">
                            <div className="mobile-mc-box">
                              <div className="mobile-mc-label">Current MC</div>
                              <div className={`mobile-mc-value ${launch._updateAnimation ? 'updating' : ''}`}>
                                ${formatNumber(launch.currentmarketcapsol_usd || 0)}
                              </div>
                            </div>
                            <div className="mobile-ath-box">
                              <div className="mobile-ath-label">ATH</div>
                              <div className="mobile-ath-value">${formatNumber(launch.peakmarketcapsol_usd || 0)}</div>
                            </div>
                          </div>
                          
                          <div className="mobile-dev-dex-row">
                            <div className="mobile-dev-box">
                              <div className="mobile-dev-label">DEV</div>
                              <div 
                                className="mobile-dev-ca clickable"
                                onClick={() => {
                                  copyToClipboard(launch.creator);
                                  navigate(`/dev/${encodeURIComponent(launch.creator)}`);
                                }}
                                title="Click to copy and view dev details"
                              >
                                {launch.creator}
                              </div>
                              <div className="mobile-bonding-rate">{parseFloat(launch.bonding_rate_percent || 0).toFixed(1)}%</div>
                              <div className="mobile-last-trade">Last trade: {formatTimeAgo(devGroup.lastTradeTimestamp)}</div>
                            </div>
                          </div>
                          
                          <div className="mobile-actions-row">
                            <a 
                              href={`https://pump.fun/coin/${launch.tokenmint}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mobile-action-btn pump-btn"
                            >
                              🚀 Pump.fun
                            </a>
                            <a 
                              href={`https://neo.bullx.io/terminal?chainId=1399811149&address=${launch.tokenmint}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mobile-action-btn bullx-btn"
                            >
                              📊 BullX
                            </a>
                          </div>

                          {/* Dev Trades Table - Mobile */}
                          <div className="dev-trades-table-mobile">
                            <div className="mobile-trades-header">Dev Trades</div>
                            <div className="mobile-trades-list-table">
                              {trades.slice(0, 5).map((trade, idx) => (
                                <div key={idx} className={`mobile-trade-row ${trade.txType === 'buy' ? 'trade-row-buy' : 'trade-row-sell'}`}>
                                  <div className="mobile-trade-row-left">
                                    <span className={`mobile-trade-type-badge ${trade.txType === 'buy' ? 'trade-buy' : 'trade-sell'}`}>
                                      {trade.txType.toUpperCase()}
                                    </span>
                                    <span className="mobile-trade-sol-amount">{trade.solAmount?.toFixed(4) || '0.0000'} SOL</span>
                                  </div>
                                  <div className="mobile-trade-row-right">
                                    <div className="mobile-trade-mc-small">
                                      {trade.marketCapSol > 0 ? (() => {
                                        // Berechne SOL-Preis aus Launch-Daten oder verwende Standardwert
                                        const solPrice = launch.currentmarketcapsol_usd && launch.currentmarketcapsol 
                                          ? parseFloat(launch.currentmarketcapsol_usd) / parseFloat(launch.currentmarketcapsol)
                                          : 150; // Fallback SOL-Preis
                                        const marketCapUsd = trade.marketCapSol * solPrice;
                                        return `MC: $${formatNumber(marketCapUsd)}`;
                                      })() : ''}
                                    </div>
                                    <div className="mobile-trade-time-small">{formatTimeAgo(trade.timestamp)}</div>
                                  </div>
                                </div>
                              ))}
                              {trades.length > 5 && (
                                <div className="mobile-trades-more">+{trades.length - 5} more trades</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default ActiveDevPage;

